import type { MediaIdResult, QuestionMedia } from '@/types/api';

/** The longest a question's media may stand on screen, so the question still fits under it. */
export const MEDIA_MAX_VH = 34;

/** A clip's window: where it starts, and where it stops (null: its end). */
export const clipWindow = (media: QuestionMedia): { start: number; end: number | null } => {
  const start = Math.max(0, Number(media.start) || 0);
  const end = Number(media.end);
  return { start, end: Number.isFinite(end) && end > start ? end : null };
};

/** Where playback stands once kept to the clip's window (0 at its start). */
export const clampToWindow = (time: number, media: QuestionMedia): number => {
  const { start, end } = clipWindow(media);
  if (time < start) return start;
  if (end != null && time > end) return end;
  return time;
};

/** Words for what the media is, for a screen reader and a missing image. */
export const mediaLabel = (media: QuestionMedia): string => {
  if (media.name) return media.name;
  switch (media.mediaType) {
    case 'VIDEO':
      return 'A home movie';
    case 'AUDIO':
      return 'A recording';
    case 'DOCUMENT':
      return 'A document';
    default:
      return 'A family photo';
  }
};

const firstUpper = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * What The Archivist understood from an answer, as short chips: who's in it,
 * what was happening, when and where, then who else was there.
 */
export const resultChips = (result?: MediaIdResult | null): string[] => {
  if (!result?.chips) return [];
  const { people, what, when, where, alsoThere } = result.chips;
  const chips = [
    ...(people ?? []),
    what ? firstUpper(what) : null,
    when ? firstUpper(when) : null,
    where ? firstUpper(where) : null,
  ].filter((c): c is string => !!c && !!c.trim());
  if (alsoThere?.length) chips.push(`Also there: ${alsoThere.join(', ')}`);
  return chips;
};
